import type { AccountRiskProfile, AssessedRiskRating, RiskRatingHistoryEntry } from '@gcv/shared';
import { RiskRatingSource } from '@gcv/shared';

import { RISK_RATING_SOURCE_LABELS } from 'ui/apps/fi/shared/types';

/** The account's rating beneath any override. */
export interface ComputedRating {
  rating: AssessedRiskRating;
  score?: number;
  source: RiskRatingSource;
  sourceLabel: string;
  /** When this rating was recorded, not the override. */
  setOn: string;
  /** False when nothing recomputed since the override, so this is the rating it replaced. */
  isCurrent: boolean;
}

const OVERRIDE_SOURCES: RiskRatingSource[] = [
  RiskRatingSource.ManualOverride,
  RiskRatingSource.OverrideRemoved
];

const newestFirst = (entries: RiskRatingHistoryEntry[]) =>
  [...entries].sort((a, b) => b.changed_on.localeCompare(a.changed_on));

/** Newest suppressed change, else the last pre-override rating; undefined if only ever overridden. */
export const getComputedRating = (profile?: AccountRiskProfile): ComputedRating | undefined => {
  if (!profile) {
    return undefined;
  }

  const history = newestFirst(profile.history ?? []);

  const suppressed = history.find((entry) => !!entry.suppressed_by_source && !!entry.would_be_new_rating);

  if (suppressed?.would_be_new_rating) {
    return {
      rating: suppressed.would_be_new_rating,
      score: suppressed.score,
      source: suppressed.source,
      sourceLabel: RISK_RATING_SOURCE_LABELS[suppressed.source] ?? '',
      setOn: suppressed.changed_on,
      isCurrent: true
    };
  }

  const previous = history.find(
    (entry) => !entry.suppressed_by_source && !OVERRIDE_SOURCES.includes(entry.source)
  );

  if (!previous) {
    return undefined;
  }

  return {
    rating: previous.new_rating,
    score: previous.score,
    source: previous.source,
    sourceLabel: RISK_RATING_SOURCE_LABELS[previous.source] ?? '',
    setOn: previous.changed_on,
    isCurrent: false
  };
};
